import './fix-alert-z-index-on-web.css'
import { AlertDialog, Button, View, createContext, useEvent } from 'tamagui'
import type { AlertDialogContentProps } from 'tamagui'
import { useRef, useState } from 'react'
import type { WebCompatibleAlertOptions } from '@/src/components/ui/elements/dialogs/useAlert'
import { fontFamilies } from '@/src/common/theme'
import { StyleSheet } from 'react-native'

type AlertButton = {
  title: string
  action: () => void
  style: 'default' | 'cancel' | 'destructive'
}

type AlertParam = {
  title: string
  message: string
  buttons: AlertButton[]
  options?: WebCompatibleAlertOptions
}

interface AlertDialogContextProps {
  open: boolean
  title: string
  message: string
  buttons: AlertButton[]
  alert: (param: AlertParam) => void
}

export const [AlertStateProvider, useWebAlert] = createContext<AlertDialogContextProps>('Alert', {
  open: false,
  title: '',
  message: '',
  buttons: [],
  alert: () => {},
})

export type UseWebAlert = typeof useWebAlert

type AlertProps = AlertDialogContentProps & {
  children: React.ReactNode
}
export function AlertProvider({ children, ...rest }: AlertProps) {
  const [open, setOpenNotAccountingForDismiss] = useState(false)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState('')
  const [buttons, setButtons] = useState<AlertButton[]>([])
  const [closeableByOverlay, setCloseableByOverlay] = useState(true)
  const dismissRef = useRef<NonNullable<AlertParam['options']>['onDismiss']>(null)

  function setOpen(value: boolean) {
    setOpenNotAccountingForDismiss(value)
    !value && dismissRef.current?.()
  }

  const alert = useEvent(
    ({
      title,
      message,
      buttons,
      options: { closeableByOverlay = true, onDismiss } = {},
    }: AlertParam) => {
      setTitle(title)
      setMessage(message)
      setButtons(buttons)
      setOpen(true)
      setCloseableByOverlay(closeableByOverlay)
      dismissRef.current = onDismiss
    },
  )

  const closeDialog = useEvent(closeableByOverlay ? () => setOpen(false) : () => {})

  return (
    <AlertStateProvider open={open} message={message} title={title} buttons={buttons} alert={alert}>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialog.Portal id="id-for-yeeting-the-root-z-index">
          <AlertDialog.Overlay
            onPress={closeDialog}
            key="overlay"
            animation="quick"
            enterStyle={{ opacity: 0 }}
            exitStyle={{ opacity: 0 }}
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            {...(closeableByOverlay ? undefined : { pointerEvents: 'none' })}
          />
          <AlertDialog.Content
            style={{
              width: 500,
              maxWidth: '88%',
              border: 'none',
              paddingBottom: 7,
              paddingLeft: 18,
              paddingRight: 18,
              paddingTop: 13,
              borderRadius: 16,
            }}
            gap="$1"
            key="content"
            animation={['quick', { opacity: { overshootClamping: true } }]}
            enterStyle={{ x: 0, y: -10, opacity: 0, scale: 0.95 }}
            exitStyle={{ x: 0, y: 10, opacity: 0, scale: 0.95 }}
            $theme-dark={{ borderColor: '$color6', borderWidth: 1 }}
            {...rest}
          >
            <View>
              <AlertDialog.Title size="$3" style={styles.alertDialogTitle}>
                {title}
              </AlertDialog.Title>
              <AlertDialog.Description style={styles.alertDialogDescription}>
                {message}
              </AlertDialog.Description>
            </View>
            <View flexDirection="row" gap="$4" style={{ justifyContent: 'flex-end' }}>
              {buttons.map((button, index) => {
                const Base = button.style === 'cancel' ? AlertDialog.Cancel : AlertDialog.Action
                const color = button.style === 'destructive' ? '$red10' : '$color'
                return (
                  <Base key={index} asChild>
                    <Button
                      chromeless
                      onPress={button.action}
                      ref={(b) => (b && button.style === 'destructive' ? b.focus() : undefined)}
                    >
                      <Button.Text color={color}>{button.title}</Button.Text>
                    </Button>
                  </Base>
                )
              })}
            </View>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog>
      {children}
    </AlertStateProvider>
  )
}

const styles = StyleSheet.create({
  alertDialogTitle: { fontFamily: fontFamilies['PlusJakartaSans-Bold'], fontWeight: 700 },
  alertDialogDescription: {
    fontFamily: fontFamilies['PlusJakartaSans-Regular'],
    fontWeight: 400,
    whiteSpace: 'pre-wrap',
  },
})
